import React, { useMemo, useEffect, useState } from 'react';
import Pagination from 'react-bootstrap/Pagination';
import Spinner from 'react-bootstrap/Spinner';
import { BsChevronDown, BsChevronLeft, BsChevronRight } from 'react-icons/bs';

import BayDropdown from '../BayDropdown';
import BaySelect from '../BaySelect';

// eslint-disable-next-line
import useMediaQuery from '../utilities/hook/useMediaQuery';
// eslint-disable-next-line
import './BayPagination.scss';

interface TranslateOptions {
	defaultValue?: string;
	[key: string]: unknown;
}

interface BayPaginationProps {
	pageSizeOptions?: number[];
	selectedPageSize?: number;
	totalRowCount?: number;
	totalPages?: number;
	activePage?: number;
	handlePageChange?: (_pageNumber: number, _pageSize: number) => void;
	previousLabel?: React.ReactNode;
	nextLabel?: React.ReactNode;
	isModalOrDrawer?: boolean;
	modalOrDrawerRef?: React.RefObject<HTMLElement> | HTMLElement;
	/**
	 * Force the condensed layout regardless of viewport, for a pagination sitting
	 * in a narrow slot inside a wide page. Ported from master's #937, where it was
	 * `isCondensed = isTabSize || isCompactSlotSize`; this branch's equivalent of
	 * that condensed mode is the tab-size layout.
	 */
	isCompactSlotSize?: boolean;
	pageSizeSelectorLabel?: string;
	isPageSizeDropDown?: boolean;
	showPageSizeSelector?: boolean;
	showLoadMore?: boolean;
	handleLoadMore?: () => void;
	disabled?: boolean;
	t?: (_key: string, _options?: TranslateOptions) => string;
	pageSizeUnit?: string;
	isLoading?: boolean;
	hidePageNavigation?: boolean;
	hideSelectedPaginationValue?: boolean;
}

interface PageSizeOption {
	label: string;
	value: number;
}

// Type guard to check if a value is a React.RefObject
const isRefObject = (
	value: React.RefObject<HTMLElement> | HTMLElement | undefined,
): value is React.RefObject<HTMLElement> => {
	return !!value && typeof value === 'object' && 'current' in (value as object);
};

const BayPagination: React.FC<BayPaginationProps> = (props) => {
	const {
		pageSizeOptions = [10, 20, 30, 40, 50],
		selectedPageSize = 1,
		totalRowCount = 100,
		totalPages = 10,
		activePage = 1,
		handlePageChange = () => {},
		previousLabel = '<', // add bootstrap Icon or string
		nextLabel = '>', // add bootstrap Icon or string
		isModalOrDrawer = false, // Prop for Modal or Drawer to get the width of the element.
		modalOrDrawerRef, // if isModalOrDrawer is true we need to send the element ref.
		isCompactSlotSize = false,
		pageSizeSelectorLabel = '',
		isPageSizeDropDown = false,
		showPageSizeSelector = true,
		showLoadMore = false,
		handleLoadMore = () => {},
		disabled = false,
		t = (_key: string, options?: TranslateOptions) => (options?.defaultValue ?? ''),
		pageSizeUnit = 'Rows',
		isLoading = false,
		hidePageNavigation = false,
		hideSelectedPaginationValue = true,
	} = props;

	// updated breakpoints for the new pagination ui
	const isMobileViewport = useMediaQuery('(max-width: 767px)');
	const isTabViewport = useMediaQuery('(min-width: 768px) and (max-width: 1279px)');
	const isLaptopViewport = useMediaQuery('(min-width: 1280px) and (max-width: 1439px)');

	const [isTabSize, setIsTabSize] = useState<boolean>(false);
	const [isLaptopSize, setIsLaptopSize] = useState<boolean>(false);
	const [isSmallDevice, setIsSmallDevice] = useState<boolean>(false);

	// #937 (master, c1b5680c): a caller sitting in a narrow slot inside a wide page
	// can force the condensed layout without a viewport match. Master wrote this as
	// `isCondensed = isTabSize || isCompactSlotSize`; this branch's condensed mode is
	// the tab-size layout, so the flag folds in here and the laptop branch below is
	// unreachable once it is set -- the condensed branch returns first.
	const isCondensed = isTabSize || isCompactSlotSize;

	// useEffect to update the breakpoint flags based on the current screensize
	useEffect(() => {
		if (!isModalOrDrawer) {
			setIsSmallDevice(isMobileViewport);
			setIsTabSize(isTabViewport);
			setIsLaptopSize(isLaptopViewport);
		}
	}, [isModalOrDrawer, isMobileViewport, isTabViewport, isLaptopViewport]);

	const isIconLabel = useMemo(() => {
		return {
			isPreviousIcon: React.isValidElement(previousLabel),
			isNextIcon: React.isValidElement(nextLabel),
		};
	}, [previousLabel, nextLabel]);

	const paginationResizer = (): void => {
		// Handle both React ref objects and direct DOM elements
		let modalDrawerWidth: number | undefined;

		if (modalOrDrawerRef) {
			// Use type guard to check if it's a React ref object
			if (isRefObject(modalOrDrawerRef) && modalOrDrawerRef.current) {
				modalDrawerWidth = modalOrDrawerRef.current.offsetWidth;
			}
			// Check if it's a direct DOM element
			else if (modalOrDrawerRef instanceof HTMLElement) {
				modalDrawerWidth = modalOrDrawerRef.offsetWidth;
			}
		}
		if (modalDrawerWidth && modalDrawerWidth <= 400) {
			setIsSmallDevice(true);
			setIsTabSize(false); // Since <= 400 is not in tab size range
		} else if (modalDrawerWidth && modalDrawerWidth <= 850) {
			setIsSmallDevice(false);
			setIsTabSize(true);
		} else {
			setIsSmallDevice(false);
			setIsTabSize(false);
		}
	};

	useEffect(() => {
		if (isModalOrDrawer && modalOrDrawerRef) {
			const observer = new ResizeObserver(() => paginationResizer());
			let element: HTMLElement | null = null;

			// Use type guard to check if it's a React ref object
			if (isRefObject(modalOrDrawerRef) && modalOrDrawerRef.current) {
				element = modalOrDrawerRef.current;
			} else if (modalOrDrawerRef instanceof HTMLElement) {
				element = modalOrDrawerRef;
			}

			if (element) {
				observer.observe(element);
			}
			return () => observer.disconnect();
		}
	}, [isModalOrDrawer, modalOrDrawerRef]);

	// Handle page navigation
	const gotoPage = (pageNumber: number): void => {
		if (pageNumber >= 1 && pageNumber <= totalPages) {
			handlePageChange(pageNumber, selectedPageSize);
		}
	};

	const toggleOptions = useMemo<PageSizeOption[]>(() => {
		const keys = pageSizeOptions;
		const options = keys?.map((value) => {
			return {
				label: `${value} ${pageSizeUnit}`,
				value,
			};
		});
		return options || [];
	}, [pageSizeOptions, pageSizeUnit]);

	const PageSizeDropDown: React.FC = () => {
		return (
			<div className="page-size-dropdown-container">
				<BayDropdown
					alignment="start"
					items={toggleOptions.map((pageSize, index) => {
						return {
							...pageSize,
							eventKey: index.toString(),
							label: pageSize.label,
							onClick: ({ value }: { value: number }) => handlePageChange(1, value),
						};
					})}
				>
					<div className="page-size-dropdown user-select-none">
						<div className="dropdown-label ps-1 pe-2">
							{toggleOptions?.find((pageSize) => pageSize.value === selectedPageSize)?.label}
						</div>
						<BsChevronDown size={24} className="p-1" aria-hidden="true" focusable="false" />
					</div>
				</BayDropdown>
			</div>
		);
	};

	const PageSizeSelector: React.FC = () => {
		const portalTarget = typeof window !== 'undefined' ? document.body : undefined;

		return (
			<div className="page-size-select">
				<BaySelect
					allowSearch={false}
					hideSelectedOne={hideSelectedPaginationValue}
					controlledValue={
						toggleOptions.find(({ value }) => value === selectedPageSize) ||
						toggleOptions[0]
					}
					defaultValue={toggleOptions[0]}
					menuPortalTarget={portalTarget}
					menuPosition="fixed"
					styles={{
						menuPortal: (base: Record<string, unknown>) => ({
							...base,
							// updated the z-index to prevent the pagination dropdown from being hidden behind the modal when the component is used inside modals.
							zIndex: 'calc(var(--bs-modal-zindex, 1055) + 5)',
						}),
					}}
					disabled={disabled || isLoading}
					menuPlacement="auto"
					isClearable={false}
					optionsArray={toggleOptions}
					placeholder={null}
					onChangeFunc={({ value }: { value: number }) => {
						handlePageChange(1, value);
					}}
				/>
			</div>
		);
	};

	const PageSizeSelectorWithResults: React.FC = () => {
		return (
			<div
				className='d-flex align-items-center gap-3 flex-wrap flex-md-nowrap justify-content-center justify-content-md-start'
				style={{ minWidth: 'fit-content' }}
			>
				{isLoading ? (
					<>
						<div className="d-flex align-items-center gap-2">
							<Spinner animation="border" size="sm" />
							<small className="text-muted">Loading...</small>
						</div>
						<div className='text-200'>|</div>
						<div>{pageSizeSelectorLabel}</div>
						{isPageSizeDropDown ? <PageSizeDropDown /> : <PageSizeSelector />}
					</>
				) : (
					<>
						<div className="">
							{`${totalRowCount > 0 ? new Intl.NumberFormat('en-US').format(totalRowCount) : 0} Total Records`}
						</div>
						<div className='text-200'>|</div>
						<div>{pageSizeSelectorLabel}</div>
						{isPageSizeDropDown ? <PageSizeDropDown /> : <PageSizeSelector />}
					</>
				)}
			</div>
		);
	};

	// Render pagination items with ellipses
	const renderPaginationItems = (): React.ReactElement[] => {
		const items: React.ReactElement[] = [];

		const addPageItem = (page: number, isActive = false): void => {
			items.push(
				<Pagination.Item
					key={page}
					active={isActive}
					onClick={() => gotoPage(page)}
				>
					{page}
				</Pagination.Item>,
			);
		};

		const addEllipsis = (key: string, goto: number): void => {
			items.push(
				<Pagination.Ellipsis key={key} onClick={() => gotoPage(goto)} />,
			);
		};

		const renderPages = (start: number, end: number, active: number): void => {
			for (let i = start; i <= end; i++) {
				addPageItem(i, i === active);
			}
		};

		// render the pagination grid dynamically based on the current screen size
		if (isCondensed) {
			if (totalPages <= 3) {
				renderPages(1, totalPages, activePage);
				return items;
			}

			const middlePage = Math.min(Math.max(activePage, 2), totalPages - 1);

			addPageItem(1, activePage === 1);

			if (middlePage > 2) {
				addEllipsis('start-ellipsis', middlePage - 1);
			}

			addPageItem(middlePage, middlePage === activePage);

			if (middlePage < totalPages - 1) {
				addEllipsis('end-ellipsis', middlePage + 1);
			}

			addPageItem(totalPages, totalPages === activePage);

			return items;
		}

		if (isLaptopSize) {
			const windowLength = 4;

			if (totalPages <= windowLength + 2) {
				renderPages(1, totalPages, activePage);
				return items;
			}

			let windowStart = activePage - 2;
			let windowEnd = windowStart + windowLength - 1;

			if (windowStart < 2) {
				windowStart = 2;
				windowEnd = windowStart + windowLength - 1;
			}

			if (windowEnd > totalPages - 1) {
				windowEnd = totalPages - 1;
				windowStart = windowEnd - windowLength + 1;
			}

			addPageItem(1, activePage === 1);

			if (windowStart > 2) {
				addEllipsis('start-ellipsis', windowStart - 1);
			}

			renderPages(windowStart, windowEnd, activePage);

			if (windowEnd < totalPages - 1) {
				addEllipsis('end-ellipsis', windowEnd + 1);
			}

			addPageItem(totalPages, totalPages === activePage);

			return items;
		}

		if (totalPages <= 10) {
			renderPages(1, totalPages, activePage);
		} else {
			addPageItem(1, activePage === 1);

			if (activePage > 6) {
				const ellipsisTarget =
					activePage > totalPages - 8
						? totalPages - 8
						: Math.max(activePage - 3, 2);
				addEllipsis('start-ellipsis', ellipsisTarget);
				if (activePage <= totalPages - 5) {
					renderPages(activePage - 3, activePage + 2, activePage);
				} else {
					renderPages(totalPages - 7, totalPages - 1, activePage);
				}
			}
			if (activePage <= 6) {
				renderPages(2, 8, activePage);
			}
			if (activePage < totalPages - 4) {
				const ellipsisTarget =
					activePage < 6 ? 9 : Math.min(activePage + 3, totalPages - 1);
				addEllipsis('end-ellipsis', ellipsisTarget);
			}
			addPageItem(totalPages, totalPages === activePage);
		}
		return items;
	};

	const isPrevDisabled = disabled || activePage <= 1;
	const isNextDisabled = disabled || activePage >= totalPages;

	const mobileArrowClass = 'btn btn-outline-light d-flex align-items-center justify-content-center p-2 text-700 border border-200 rounded-2';

	return (
		<>
			<div
				className={`bay-pagination w-100 ${isSmallDevice ? 'd-none' : 'd-block'}`}
			>
				{/* Pagination controls */}
				<div
					className={`d-flex flex-sm-row gap-sm-0 gap-3 flex-column align-items-center bay-pagination-wrapper ${totalRowCount > 0 && !hidePageNavigation ? 'justify-content-between' : 'justify-content-end'}`}
				>
					{!showLoadMore && !isLoading && totalRowCount > 0 && !hidePageNavigation && (
						<Pagination className="mb-0 col-auto justify-content-start">
							<div
								aria-disabled={activePage === 1}
								className={`pagination-prev d-flex align-items-center ${activePage === 1 ? 'disabled pe-none' : ''} ${isIconLabel.isPreviousIcon || isCondensed ? 'icon-label' : ''}`}
								onClick={() => activePage > 1 && gotoPage(activePage - 1)}
							>
								{isCondensed
									? <BsChevronLeft size={16} aria-hidden="true" focusable="false" />
									: previousLabel}
							</div>
							{renderPaginationItems()}
							<div
								aria-disabled={activePage === totalPages}
								className={`pagination-next d-flex align-items-center ${activePage === totalPages ? 'disabled pe-none' : ''} ${isIconLabel.isNextIcon || isCondensed ? 'icon-label' : ''}`}
								onClick={() => activePage < totalPages && gotoPage(activePage + 1)}
							>
								{isCondensed
									? <BsChevronRight size={16} aria-hidden="true" focusable="false" />
									: nextLabel}
							</div>
						</Pagination>
					)}
					{showLoadMore && (
						<>
							<div className="col-4" />
							<div className="col-4 d-flex justify-content-center">
								<button
									className="btn btn-outline-secondary"
									disabled={disabled}
									onClick={handleLoadMore}
								>
									{t('loadMore')}
								</button>
							</div>
							<div className="col-4" />
						</>
					)}
					{showPageSizeSelector && !showLoadMore && (
						<div className="col-auto d-flex justify-content-end">
							<PageSizeSelectorWithResults />
						</div>
					)}
				</div>
			</div>
			<div
				className={`bay-pagination-mobile ${!isSmallDevice ? 'd-none' : 'd-block'}`}
			>
				{/* mobile pagination is now two boxed arrows around page x of y */}
				{!showLoadMore && !isLoading && totalRowCount > 0 && !hidePageNavigation && (
					<div className="d-flex align-items-center justify-content-center gap-3">
						<button
							type="button"
							aria-label={t('previousPage', { defaultValue: 'Previous page' })}
							className={`${mobileArrowClass} ${isPrevDisabled ? 'opacity-50' : 'cursor-pointer'}`}
							disabled={isPrevDisabled}
							onClick={() => gotoPage(activePage - 1)}
						>
							<BsChevronLeft size={24} aria-hidden="true" focusable="false" />
						</button>
						<div className="pagination-mobile-status text-600 text-nowrap">
							{t('paginationPageStatus', {
								defaultValue: `Page ${activePage} of ${totalPages}`,
								activePage,
								totalPages,
							})}
						</div>
						<button
							type="button"
							aria-label={t('nextPage', { defaultValue: 'Next page' })}
							className={`${mobileArrowClass} ${isNextDisabled ? 'opacity-50' : 'cursor-pointer'}`}
							disabled={isNextDisabled}
							onClick={() => gotoPage(activePage + 1)}
						>
							<BsChevronRight size={24} aria-hidden="true" focusable="false" />
						</button>
					</div>
				)}
				{showLoadMore && (
					<div className="d-flex align-items-center justify-content-center gap-3">
						<button
							className="btn btn-outline-secondary"
							disabled={disabled}
							onClick={handleLoadMore}
						>
							{t('loadMore')}
						</button>
					</div>
				)}
			</div>
		</>
	);
};

export default BayPagination;
